"use client";
import { Box, Container, Text } from "@mantine/core";
import Image from "next/image";
import { Link, usePathname } from "@/i18n/navigation";
import classes from "./Footer.module.css";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

export default function Footer() {
  const t = useTranslations("footer");
  const params = useParams();
  const pathname = usePathname();

  const isPublicProfile = !!params?.username;
  const isDrapingWizard = pathname?.includes("advanced-draping-wizard");
  const isColorAnalysisResult = pathname?.includes("ai-color-analysis/result");
  const isAnalysisWizard = pathname?.includes("analysis-wizard");

  // Hide footer on conversion-focused pages to eliminate exit points
  if (isPublicProfile || isDrapingWizard || isColorAnalysisResult || isAnalysisWizard) {
    return null;
  }

  // Slim hub-and-spoke footer: money pages + free tools + content HUBS only.
  // Individual types/palettes/articles are one hop away via their hubs, and
  // the 5 pages the old footer solely linked now have contextual in-content
  // links instead (see skin-tone-test, how-to-do-color-analysis, aesthetic-quiz).
  const data = [
    {
      title: "AI Styling Tools",
      links: [
        { label: t("aiColorAnalysis"), link: "/ai-color-analysis", primary: true },
        { label: t("kibbeBodyTypeTest"), link: "/kibbe-body-type-test" },
        { label: t("aiOutfitGenerator"), link: "/ai-outfit-generator" },
        { label: "AI Personal Stylist", link: "/style-club" },
        { label: "Complete Style Analysis", link: "/style-profile-bundle" },
      ],
    },
    {
      title: "Free Tools",
      links: [
        { label: t("freeColorQuiz"), link: "/color-analysis-quiz" },
        { label: t("skinToneTest"), link: "/skin-tone-test" },
        { label: t("undertoneQuiz"), link: "/undertone-quiz" },
        { label: t("goldOrSilverTest"), link: "/gold-or-silver-jewelry-test" },
        { label: t("colorAnalysisForMen"), link: "/color-analysis-for-men" },
        { label: t("virtualColorDraping"), link: "/virtual-color-draping" },
        { label: t("aiHairColorChanger"), link: "/ai-hair-color-try-on" },
        { label: "All Free Tools", link: "/tools" },
      ],
    },
    {
      title: "Guides",
      links: [
        { label: t("allColorPalettes"), link: "/color-palette" },
        { label: t("colorSeasonsChart"), link: "/color-seasons-chart" },
        { label: "Color Season Outfits", link: "/outfits" },
        { label: t("all13KibbeTypes"), link: "/kibbe-body-types-guide" },
        { label: t("kibbeOutfitsGuide"), link: "/kibbe-outfits" },
      ],
    },
    {
      title: t("company"),
      links: [
        { label: t("about"), link: "/about" },
        { label: t("contact"), link: "/contact" },
        { label: t("privacyPolicy"), link: "/privacy" },
        { label: t("termsOfService"), link: "/terms" },
      ],
    },
  ];

  const groups = data.map((group) => {
    const links = group.links.map((link, index) => (
      <Link key={index} className={classes.link} href={link.link}>
        {link.label}
      </Link>
    ));

    return (
      <div className={classes.wrapper} key={group.title}>
        <Text className={classes.title}>{group.title}</Text>
        {links}
      </div>
    );
  });

  return (
    <footer className={classes.footer}>
      <Container className={classes.inner} size="xl">
        <div className={classes.logo}>
          <Box display="flex">
            <Image
              src="/palette.png"
              alt="ColorAnalysisTech Logo"
              width={28}
              height={28}
            />
            <Text ml="5" size="lg" fw="500">
              {t("companyName")}
            </Text>
          </Box>
          <Text size="xs" className={classes.description} style={{ color: "#64748b" }}>
            {t("tagline")}
          </Text>
          <Text fz={"xs"} style={{ color: "#94a3b8" }}>
            {t("madeBy")}
          </Text>
          <Text fz={"xs"} mt={4} style={{ color: "#94a3b8" }}>
            <a
              href="https://gizmoanalytics.io/?ref=badge"
              target="_blank"
              rel="noopener"
              style={{ color: "#94a3b8", textDecoration: "none" }}
            >
              Powered by Gizmo
            </a>
          </Text>
        </div>
        <div className={classes.groups}>{groups}</div>
      </Container>
    </footer>
  );
}
